import './styles/base.css';
import './styles/identity.css';
import './styles/app.css';

import {formatEth, shortAddress} from '@colosseum/shared';
import {config, loadRemoteConfig} from './config.js';
import {api} from './api.js';
import {currentRoute, navigate, onRouteChange, type Route} from './router.js';
import {startPolling, state, subscribe} from './store.js';
import {$, html, onAction, raw, setHtml, setText} from './ui/dom.js';
import {closeModal, modal, notify} from './ui/feedback.js';
import {connect, disconnect, discoverProviders, onWalletChange, onRightChain, switchChain, wallet, WalletError} from './wallet.js';
import {claim, describeError} from './tx.js';
import {txProgress} from './ui/feedback.js';

import {
  arenaView, mountArena, paint as paintArena, runDraw, runExecute, setQuery, setSort, toggleMotion,
} from './views/arena.js';
import {mountToken, tokenView, setSide, setSlippage, applyPreset, applyMax, submitTrade, collectFees} from './views/token.js';
import {launchView, mountLaunch} from './views/launch.js';
import {leaderboardView, mountLeaderboard, setWindow} from './views/leaderboard.js';
import {docsView} from './views/docs.js';

const app = $('#app')!;

app.innerHTML = html`<header>
    <a class="brand" href="#/" aria-label="Colosseum home">
      <img src="/assets/wordmark.png" alt="COLOSSEUM" width="901" height="122">
    </a>
    <nav aria-label="Main">
      <a href="#/" data-nav="arena">Arena</a>
      <a href="#/launch" data-nav="launch">Launch</a>
      <a href="#/leaderboard" data-nav="leaderboard">Leaderboard</a>
      <a href="#/docs" data-nav="docs">How it works</a>
    </nav>
    <div class="search">
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/>
      </svg>
      <input type="search" id="search" placeholder="Search name, ticker or address" aria-label="Search tokens">
      <kbd>/</kbd>
    </div>
    <button class="primary" id="wallet" data-action="wallet">Connect</button>
  </header>
  <div id="banner"></div>
  <main class="page" id="main"></main>
  <dialog id="modal" aria-labelledby="modal-title">
    <div class="dialog-head"><h2 id="modal-title"></h2><button class="close" data-action="close" aria-label="Close">×</button></div>
    <div class="dialog-body" id="modal-body"></div>
  </dialog>
  <div id="toast" class="toast" role="status" hidden></div>
  <footer class="footer">
    <span>COLOSSEUM · every four hours, the pool buys one token and burns it</span>
    <span>
      <a href="#/docs">How the draw works</a>
      <a href="https://github.com/99Zeyad/colosseum" target="_blank" rel="noopener">Contracts</a>
      <span id="source-note"></span>
    </span>
  </footer>`;

// ------------------------------------------------------------------ routing

let teardown: (() => void) | undefined;

async function render(route: Route): Promise<void> {
  teardown?.();
  teardown = undefined;
  for (const a of document.querySelectorAll<HTMLElement>('[data-nav]')) {
    a.classList.toggle('active', a.dataset.nav === route.name);
  }
  const main = $('#main')!;
  switch (route.name) {
    case 'token':
      setHtml(main, tokenView(route.address));
      teardown = await mountToken(route.address);
      break;
    case 'launch':
      setHtml(main, launchView());
      teardown = mountLaunch();
      break;
    case 'leaderboard':
      setHtml(main, leaderboardView());
      teardown = await mountLeaderboard();
      break;
    case 'docs':
      setHtml(main, docsView());
      break;
    default:
      setHtml(main, arenaView());
      teardown = mountArena();
  }
  window.scrollTo(0, 0);
}

// ------------------------------------------------------------------ header state

function paintWallet(): void {
  const btn = $('#wallet');
  if (!btn) return;
  if (!wallet.address) {
    setText(btn, 'Connect');
    btn.classList.remove('warn-btn');
    return;
  }
  if (!onRightChain()) {
    setText(btn, 'Wrong network');
    btn.classList.add('warn-btn');
    return;
  }
  btn.classList.remove('warn-btn');
  setText(btn, shortAddress(wallet.address));
}

/**
 * The banner is the only place the site editorialises about its own health. It
 * says what is actually true — no deployment, indexer unreachable, wrong
 * network — rather than rendering zeroes as if they were data.
 */
function paintBanner(): void {
  const el = $('#banner');
  if (!el) return;
  const bits: string[] = [];
  if (!config.addresses) {
    bits.push(
      html`<div class="banner banner-warn">
        <b>Not deployed.</b> No contract addresses are built into this site for this chain yet, so nothing can be
        traded or launched. The contracts are built and tested; they have not been deployed.
      </div>`,
    );
  } else if (config.mismatch) {
    bits.push(
      html`<div class="banner banner-warn">
        <b>Configuration mismatch.</b> ${config.mismatch} This site sends transactions only to the contracts it was
        built for, so nothing can be traded or launched until the two agree.
      </div>`,
    );
  }
  if (state.source === 'chain') {
    bits.push(
      html`<div class="banner">The indexer is unreachable. The pool and the countdown are read straight from the
        chain; charts, holders and history are unavailable until it is back.</div>`,
    );
  } else if (state.source === 'down') {
    bits.push(html`<div class="banner banner-warn">Cannot reach the indexer or the chain.</div>`);
  }
  if (wallet.address && !onRightChain()) {
    bits.push(
      html`<div class="banner banner-warn">Your wallet is on chain ${wallet.chainId}. Colosseum is on
        ${config.chainName} (${config.chainId}).
        <button class="link" data-action="switch-chain">Switch</button></div>`,
    );
  }
  setHtml(el, bits.join(''));
}

function paintSourceNote(): void {
  const el = $('#source-note');
  if (!el) return;
  setText(
    el,
    state.source === 'live' ? '' : state.source === 'chain' ? 'reading the chain directly' : 'offline',
  );
}

// ------------------------------------------------------------------ actions

onAction(app, {
  wallet: () => void openWallet(),
  close: () => closeModal(),
  motion: () => toggleMotion(),
  'run-draw': () => void runDraw(),
  'run-execute': () => void runExecute(),
  'switch-chain': () => void doSwitch(),
  sort: (el) => setSort(el.dataset.sort ?? 'fees'),
  side: (el) => setSide((el.dataset.side as 'buy' | 'sell') ?? 'buy'),
  slip: (el) => setSlippage(BigInt(el.dataset.bps ?? '100')),
  preset: (el) => applyPreset(el.dataset.value ?? ''),
  max: () => applyMax(),
  trade: () => void submitTrade(),
  collect: (el) => void collectFees(el.dataset.token ?? ''),
  lb: (el) => void setWindow((el.dataset.window as 'period' | 'all') ?? 'all'),
  claim: () => void doClaim(),
  connect: (el) => void doConnect(el.dataset.uuid ?? ''),
  disconnect: () => {
    disconnect();
    closeModal();
    notify('Disconnected.');
  },
  copy: (el) => {
    const v = el.dataset.copy ?? '';
    void navigator.clipboard?.writeText(v).then(
      () => notify('Copied.'),
      () => notify('Could not copy.', 'error'),
    );
  },
});

async function openWallet(): Promise<void> {
  if (wallet.address && !onRightChain()) return doSwitch();
  if (wallet.address) return openAccount();
  const providers = discoverProviders();
  if (providers.length === 0) {
    modal(
      'Connect a wallet',
      html`<p class="wallet-note">No wallet was found in this browser. Install one that can add a custom EVM chain,
        then reload this page.</p>
      <p class="form-note">Colosseum never asks for a seed phrase or a private key, and there is no account to
        create. Connecting only shares your address.</p>`,
    );
    return;
  }
  modal(
    'Connect a wallet',
    html`${raw(
      providers
        .map((p) => {
          // The icon is whatever the wallet announced; only an inline image or an https URL is used.
          const icon = /^(data:image\/|https:\/\/)/.test(p.info.icon) ? p.info.icon : '';
          return html`<button class="wallet-option" data-action="connect" data-uuid="${p.info.uuid}">
            <span>${raw(icon ? html`<img src="${icon}" alt="" width="20" height="20" referrerpolicy="no-referrer">` : '')} ${p.info.name}</span>
            <span>→</span>
          </button>`;
        })
        .join(''),
    )}
    <p class="form-note">Connecting shares your address. Every action on this site is a transaction you sign
      yourself; the site holds no key and there is no server in the path.</p>`,
  );
}

async function doConnect(uuid: string): Promise<void> {
  try {
    const provider = discoverProviders().find((p) => p.info.uuid === uuid);
    await connect(provider);
    closeModal();
    if (!onRightChain()) await doSwitch();
  } catch (e) {
    notify(e instanceof WalletError ? e.message : describeError(e), 'error');
  }
}

async function doSwitch(): Promise<void> {
  try {
    await switchChain();
    notify(`Switched to ${config.chainName}.`, 'success');
  } catch (e) {
    notify(describeError(e), 'error');
  }
}

async function openAccount(): Promise<void> {
  const address = wallet.address!;
  modal('Your account', html`<p class="wallet-note mono">${address}</p><p class="muted">Loading…</p>`);
  try {
    const view = await api.creator(address);
    const claimable = BigInt(view.claimableWei);
    modal(
      'Your account',
      html`<p class="wallet-note mono">${address}</p>
      <div class="account-claim">
        <div>
          <small>CLAIMABLE</small>
          <b>${formatEth(claimable, {places: 6})} ETH</b>
          <span class="muted">0.5% of the ETH leg of every trade of a token you launched, forever.</span>
        </div>
        <button class="primary" data-action="claim" ${raw(claimable === 0n ? 'disabled' : '')}>Claim</button>
      </div>
      ${raw(
        view.tokens.length
          ? html`<h3 class="panel-title">Your launches</h3>
            <ul class="account-tokens">${view.tokens.map(
              (t) => raw(html`<li><a href="#/token/${t.address}">${t.name} <small>$${t.symbol}</small></a>
                <span>${formatEth(BigInt(t.feesEarnedWei), {places: 6})} ETH earned</span></li>`),
            )}</ul>`
          : html`<p class="muted">You have not launched anything yet.</p>`,
      )}
      <button class="secondary full" data-action="disconnect">Disconnect</button>`,
    );
  } catch {
    modal(
      'Your account',
      html`<p class="wallet-note mono">${address}</p>
      <p class="muted">The indexer is unreachable, so your launches and claimable balance cannot be shown.</p>
      <button class="secondary full" data-action="disconnect">Disconnect</button>`,
    );
  }
}

async function doClaim(): Promise<void> {
  const p = txProgress('Claim');
  try {
    await claim((stage, detail, hash) => p.update(stage, detail, hash));
    closeModal();
    notify('Claimed.', 'success');
  } catch (e) {
    p.fail(describeError(e));
  }
}

// ------------------------------------------------------------------ boot

const search = $<HTMLInputElement>('#search');
search?.addEventListener('input', () => {
  if (currentRoute().name !== 'arena') navigate('#/');
  setQuery(search.value);
});
document.addEventListener('keydown', (e) => {
  if (
    e.key === '/' &&
    !['INPUT', 'TEXTAREA', 'SELECT'].includes((document.activeElement as HTMLElement)?.tagName) &&
    !$<HTMLDialogElement>('#modal')?.open
  ) {
    e.preventDefault();
    search?.focus();
  }
  if (e.key === 'Escape') closeModal();
});

$('#modal')?.addEventListener('click', (e) => {
  // A click on the backdrop, which is the dialog element itself.
  if (e.target === e.currentTarget) closeModal();
});

// A token image that fails to load falls back to the placeholder. Done here,
// once, rather than with an inline handler on every image, so the page carries
// no inline script at all and a strict Content Security Policy can say so.
document.addEventListener(
  'error',
  (e) => {
    const img = e.target;
    if (!(img instanceof HTMLImageElement)) return;
    const fallback = img.dataset.fallback;
    if (!fallback) return;
    delete img.dataset.fallback;
    img.src = fallback;
  },
  true,
);

subscribe(() => {
  paintBanner();
  paintSourceNote();
  if (currentRoute().name === 'arena') paintArena();
});
onWalletChange(() => {
  paintWallet();
  paintBanner();
});
onRouteChange((r) => void render(r));

await loadRemoteConfig();
paintWallet();
paintBanner();
startPolling();
await render(currentRoute());
